import type { Card, Deck, ReviewHistory, UserSettings } from '../types';
import type { GrammarTopicProgress } from '../types/quiz';
import { getDefaultLanguagePair } from '../config/languages';

export function normalizeCard(raw: Record<string, unknown>): Card {
  return {
    id: String(raw.id),
    userId: String(raw.userId ?? raw.user_id),
    deckId: String(raw.deckId ?? raw.deck_id),
    targetWord: String(raw.targetWord ?? raw.target_word),
    nativeWord: String(raw.nativeWord ?? raw.native_word),
    targetExample: String(raw.targetExample ?? raw.target_example ?? ''),
    nativeExample: String(raw.nativeExample ?? raw.native_example ?? ''),
    wordGender: (raw.wordGender ?? raw.word_gender ?? null) as Card['wordGender'],
    wordPlural: (raw.wordPlural ?? raw.word_plural ?? null) as string | null,
    wordClass: (raw.wordClass ?? raw.word_class ?? 'noun') as Card['wordClass'],
    isIrregular: Boolean(raw.isIrregular ?? raw.is_irregular),
    pronunciationIpa: String(raw.pronunciationIpa ?? raw.pronunciation_ipa ?? ''),
    imageUrl: String(raw.imageUrl ?? raw.image_url),
    imageThumbnail: String(
      raw.imageThumbnail ?? raw.image_thumbnail ?? raw.imageUrl ?? raw.image_url,
    ),
    imageSource: String(raw.imageSource ?? raw.image_source ?? 'Pexels'),
    cardOrigin: (raw.cardOrigin ?? raw.card_origin ?? null) as Card['cardOrigin'],
    imageProvenance: (raw.imageProvenance ??
      raw.image_provenance ??
      null) as Card['imageProvenance'],
    cardDirection: (raw.cardDirection ??
      raw.card_direction ??
      'native_to_target') as Card['cardDirection'],
    wordType: (raw.wordType ?? raw.word_type ?? 'concrete') as Card['wordType'],
    srsState: raw.srsState ?? raw.srs_state ?? null,
    dueDate: String(raw.dueDate ?? raw.due_date),
    lastReviewed:
      (raw.lastReviewed ?? raw.last_reviewed)
        ? String(raw.lastReviewed ?? raw.last_reviewed)
        : null,
    createdAt: String(raw.createdAt ?? raw.created_at),
    updatedAt: String(raw.updatedAt ?? raw.updated_at),
    isFavorite: Boolean(raw.isFavorite ?? raw.is_favorite),
    status: ((raw.status as string | undefined) ?? 'active') as Card['status'],
    imageBrokenAt: (raw.imageBrokenAt ?? raw.image_broken_at ?? null) as string | null,
    cardType: (raw.cardType ?? raw.card_type ?? 'word') as Card['cardType'],
    languagePair: (raw.languagePair ?? raw.language_pair ?? undefined) as string | undefined,
    synonyms: (raw.synonyms ?? undefined) as Card['synonyms'],
    wordForms: (raw.wordForms ?? raw.word_forms ?? undefined) as Card['wordForms'],
    formalVariant: (raw.formalVariant ?? raw.formal_variant ?? undefined) as string | undefined,
    informalVariant: (raw.informalVariant ?? raw.informal_variant ?? undefined) as
      string | undefined,
    usageContext: (raw.usageContext ?? raw.usage_context ?? undefined) as string | undefined,
    exampleDialogueFormal: (raw.exampleDialogueFormal ??
      raw.example_dialogue_formal ??
      undefined) as string | undefined,
    exampleDialogueInformal: (raw.exampleDialogueInformal ??
      raw.example_dialogue_informal ??
      undefined) as string | undefined,
    responseFormal: (raw.responseFormal ?? raw.response_formal ?? undefined) as string | undefined,
    responseInformal: (raw.responseInformal ?? raw.response_informal ?? undefined) as
      string | undefined,
    personalNote: (raw.personalNote ?? raw.personal_note ?? undefined) as string | undefined,
    parentCardId: (raw.parentCardId ?? raw.parent_card_id ?? undefined) as string | undefined,
    grammarPrompt: (raw.grammarPrompt ?? raw.grammar_prompt ?? undefined) as string | undefined,
  };
}

export function normalizeReviewHistory(raw: Record<string, unknown>): ReviewHistory {
  const rating = Number(raw.rating);
  return {
    id: String(raw.id),
    cardId: String(raw.cardId ?? raw.card_id),
    rating: ([1, 2, 3, 4].includes(rating) ? rating : 1) as 1 | 2 | 3 | 4,
    reviewDurationMs: Number(raw.reviewDurationMs ?? raw.review_duration_ms ?? 0),
    reviewedAt: String(raw.reviewedAt ?? raw.reviewed_at),
  };
}

export function normalizeDeck(raw: Record<string, unknown>): Deck {
  return {
    id: String(raw.id),
    name: String(raw.name),
    emoji: String(raw.emoji ?? '📚'),
    languagePair: String(raw.languagePair ?? raw.language_pair ?? getDefaultLanguagePair().code),
    cardCount: Number(raw.cardCount ?? raw.card_count ?? 0),
    isDefault: Boolean(raw.isDefault ?? raw.is_default),
    createdAt: String(raw.createdAt ?? raw.created_at),
  };
}

export function normalizeSettings(raw: Record<string, unknown>): UserSettings {
  const rawTheme = raw.theme as string;
  const theme = (
    ['light', 'dark', 'system'].includes(rawTheme) ? rawTheme : 'system'
  ) as UserSettings['theme'];
  return {
    theme,
    dailyNewCardLimit: Number(raw.dailyNewCardLimit ?? raw.daily_new_card_limit ?? 20),
    ttsEnabled: Boolean(raw.ttsEnabled ?? raw.tts_enabled ?? true),
    ttsSpeed: Number(raw.ttsSpeed ?? raw.tts_speed ?? 1),
    autoPlayTts: Boolean(raw.autoPlayTts ?? raw.auto_play_tts ?? true),
    currentLanguagePair: String(
      raw.currentLanguagePair ?? raw.current_language_pair ?? getDefaultLanguagePair().code,
    ),
    onboardingCompleted: Boolean(raw.onboardingCompleted ?? raw.onboarding_completed ?? false),
    streak: Number(raw.streak ?? 0),
    lastReviewDate:
      (raw.lastReviewDate ?? raw.last_review_date)
        ? String(raw.lastReviewDate ?? raw.last_review_date)
        : null,
    reviewMode: (raw.reviewMode ?? raw.review_mode ?? 'standard') as UserSettings['reviewMode'],
    isPro: Boolean(raw.isPro ?? raw.is_pro ?? false),
    uiLocale: String(raw.uiLocale ?? raw.ui_locale ?? 'en'),
    immersionMode: Boolean(raw.immersionMode ?? raw.immersion_mode ?? false),
    cefrLevel: (raw.cefrLevel ?? raw.cefr_level ?? null) as UserSettings['cefrLevel'],
    milestonesSeen: (raw.milestonesSeen ?? raw.milestones_seen ?? []) as string[],
    streakFreezeAvailable: Boolean(
      raw.streakFreezeAvailable ?? raw.streak_freeze_available ?? true,
    ),
    username: (raw.username ?? raw.user_name ?? null) as string | null,
    soundEffectsEnabled: Boolean(raw.soundEffectsEnabled ?? raw.sound_effects_enabled ?? true),
    xp: Number(raw.xp ?? 0),
    level: Number(raw.level ?? 1),
    totalXpEarned: Number(raw.totalXpEarned ?? raw.total_xp_earned ?? 0),
    dailyXp: Number(raw.dailyXp ?? raw.daily_xp ?? 0),
    dailyXpDate: (raw.dailyXpDate ?? raw.daily_xp_date ?? null) as string | null,
    bestCombo: Number(raw.bestCombo ?? raw.best_combo ?? 0),
    challengeStreak: Number(raw.challengeStreak ?? raw.challenge_streak ?? 0),
    lastChallengeDate: (raw.lastChallengeDate ?? raw.last_challenge_date ?? null) as string | null,
    streakBrokenAt: (raw.streakBrokenAt ?? raw.streak_broken_at ?? null) as string | null,
    streakRecoveryUsed: Boolean(raw.streakRecoveryUsed ?? raw.streak_recovery_used ?? false),
    hapticEnabled: Boolean(raw.hapticEnabled ?? raw.haptic_enabled ?? true),
    autoEasyThresholdMs: Number(raw.autoEasyThresholdMs ?? raw.auto_easy_threshold_ms ?? 1500),
    cardCreationPreset: (raw.cardCreationPreset ?? raw.card_creation_preset ?? 'fast') as
      'fast' | 'standard' | 'deep',
    cardCreationModules: (raw.cardCreationModules ?? raw.card_creation_modules ?? undefined) as
      Record<string, { enabled: boolean; expanded: boolean }> | undefined,
    imagePickerMode: (raw.imagePickerMode ?? raw.image_picker_mode ?? 'fullscreen') as
      'inline' | 'fullscreen',
    swipeSensitivity: (raw.swipeSensitivity ?? raw.swipe_sensitivity ?? undefined) as
      'low' | 'normal' | 'high' | undefined,
  };
}

export function normalizeGrammarProgress(raw: Record<string, unknown>): GrammarTopicProgress {
  return {
    guideId: String(raw.guideId ?? raw.guide_id),
    sectionIndex: Number(raw.sectionIndex ?? raw.section_index),
    totalAttempts: Number(raw.totalAttempts ?? raw.total_attempts ?? 0),
    correctAttempts: Number(raw.correctAttempts ?? raw.correct_attempts ?? 0),
    lastAttemptedAt: String(
      raw.lastAttemptedAt ?? raw.last_attempted_at ?? new Date().toISOString(),
    ),
  };
}
